import {
    Alert,
    Box,
    Button,
    Chip,
    FormControlLabel,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import * as React from 'react';

import { ErrorAlert } from './components';
import { OUTBOUND_MODES } from './labels';
import type { DeveloperOutbound, DeveloperOutboundUpdate, OutboundMode } from './types';

/** One entry per line (commas also split); trimmed, empty lines and repeats dropped. */
export const parseEntries = (text: string) => {
    const seen = new Set<string>();
    return text
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter((s) => s && !seen.has(s.toLowerCase()) && seen.add(s.toLowerCase()));
};

const LISTS = [
    { key: 'domains', label: 'Domains', hint: 'Every address at these domains, e.g. acme-tours.com.' },
    { key: 'emails', label: 'E-mail addresses', hint: 'One tester per line.' },
    { key: 'phones', label: 'Phone numbers', hint: 'International format (E.164), e.g. +447700900123.' },
] as const;

type ListKey = typeof LISTS[number]['key'];

const textsFrom = (outbound: DeveloperOutbound): Record<ListKey, string> => ({
    domains: (outbound.allowlist?.domains || []).join('\n'),
    emails: (outbound.allowlist?.emails || []).join('\n'),
    phones: (outbound.allowlist?.phones || []).join('\n'),
});

/** The sandbox's outbound mode and tester allowlist (SBX D5, O19, O28): who may receive what the
 *  sandbox sends. Partner admins (developer.outbound_manage) change it; everyone else reads it. */
const OutboundEditor = ({
    outbound,
    canManage,
    onSave,
}: {
    outbound: DeveloperOutbound;
    canManage: boolean;
    onSave: (update: DeveloperOutboundUpdate) => Promise<DeveloperOutbound>;
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const [mode, setMode] = React.useState<OutboundMode>(outbound.mode || 'outbox');
    const [texts, setTexts] = React.useState(() => textsFrom(outbound));
    const [comment, setComment] = React.useState('');
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState<unknown>(null);

    // A save (or a reload of the overview) brings the stored state: start over from it.
    React.useEffect(() => {
        setMode(outbound.mode || 'outbox');
        setTexts(textsFrom(outbound));
    }, [outbound]);

    const stored = textsFrom(outbound);
    const dirty = mode !== (outbound.mode || 'outbox')
        || LISTS.some(({ key }) => parseEntries(texts[key]).join('\n') !== parseEntries(stored[key]).join('\n'));
    const allowAllRefused = outbound.allow_all_allowed === false;

    const save = async () => {
        setSaving(true);
        setError(null);
        const update: DeveloperOutboundUpdate = {
            mode,
            allowlist: { domains: parseEntries(texts.domains), emails: parseEntries(texts.emails), phones: parseEntries(texts.phones) },
        };
        if (comment.trim()) {
            update.comment = comment.trim().slice(0, 500);
        }
        try {
            await onSave(update);
            setComment('');
            enqueueSnackbar('Outbound settings saved', { variant: 'success' });
        } catch (e) {
            setError(e);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box data-testid="outbound-editor">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                What the sandbox sends to guests (e-mails, text messages, WhatsApp) is kept in its outbox unless
                the recipient is allowed here. Your own webhooks and endpoints always receive their calls.
            </Typography>
            <RadioGroup
                value={mode}
                onChange={(ev) => setMode(ev.target.value as OutboundMode)}
                aria-label="Outbound mode"
            >
                {(['outbox', 'allowlist', 'allow_all'] as OutboundMode[]).map((value) => (
                    <FormControlLabel
                        key={value}
                        value={value}
                        disabled={!canManage || saving || (value === 'allow_all' && allowAllRefused && mode !== 'allow_all')}
                        control={<Radio size="small" />}
                        label={(
                            <Box>
                                <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>{OUTBOUND_MODES[value].label}</Typography>
                                <Typography variant="body2" component="span" color="text.secondary"> — {OUTBOUND_MODES[value].hint}</Typography>
                            </Box>
                        )}
                    />
                ))}
            </RadioGroup>
            {allowAllRefused && (
                <Alert severity="info" sx={{ my: 1 }}>
                    {outbound.allow_all_refused_reason || 'Allow all is not possible while the sandbox uses a production vendor key.'}
                </Alert>
            )}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 1.5 }}>
                {LISTS.map(({ key, label, hint }) => (
                    <TextField
                        key={key}
                        label={label}
                        helperText={hint}
                        multiline
                        minRows={3}
                        maxRows={10}
                        fullWidth
                        size="small"
                        value={texts[key]}
                        disabled={!canManage || saving}
                        onChange={(ev) => setTexts((prev) => ({ ...prev, [key]: ev.target.value }))}
                        inputProps={{ 'aria-label': label, spellCheck: false }}
                    />
                ))}
            </Stack>
            {(outbound.allowlist?.own_domains || []).length > 0 && (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5, rowGap: 1 }}>
                    <Typography variant="body2" color="text.secondary">Always allowed:</Typography>
                    {(outbound.allowlist?.own_domains || []).map((d) => <Chip key={d} size="small" label={d} variant="outlined" />)}
                </Stack>
            )}
            {canManage && (
                <>
                    <TextField
                        label="Note for the change log (optional)"
                        size="small"
                        fullWidth
                        sx={{ mt: 2 }}
                        value={comment}
                        onChange={(ev) => setComment(ev.target.value)}
                        inputProps={{ maxLength: 500 }}
                        disabled={saving}
                    />
                    {error ? <ErrorAlert error={error} /> : null}
                    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                        <Button variant="contained" onClick={save} disabled={!dirty || saving}>Save</Button>
                        <Button
                            disabled={!dirty || saving}
                            onClick={() => { setMode(outbound.mode || 'outbox'); setTexts(textsFrom(outbound)); setError(null); }}
                        >
                            Discard changes
                        </Button>
                    </Stack>
                </>
            )}
        </Box>
    );
};

export default OutboundEditor;
